import React, { useEffect, useRef, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import SearchField from '@amzn/meridian/search-field';
import AppliedFilterTags from 'src/components/shared/appliedFilterTags/appliedFilterTags';
import NewFeatureBanner from 'src/components/shared/newFeatureBanner/newFeatureBanner';
import PaginationWithDropdown from 'src/components/shared/paginationWithDropdown/paginationWithDropdown';
import ToggleFilterButton from 'src/components/shared/toggleFilterButton/toggleFilterButton';
import {
  getOpenAndClosedPlacementsBySiteId,
  getOpenPlacementsByRequesterLogin,
  removeTagAndUpdateFilters,
  setCurrentPage,
  setIsAdvancedFilterSheetOpen,
  setIsDaliUser,
  setPerPage,
  setSearchField,
  setSelectedFilterOptions,
} from 'src/store/placementOverviewPageSlice';
import { setIsSiteSelectionDisabled, setSelectedSite } from 'src/store/siteSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { setPreferredSite } from 'src/store/userSlice';

import './placementsOverview.scss';

import AdvancedFilterSheet from './advancedFilterSheet/advancedFilterSheet';
import PlacementOverviewTable from './placementOverviewTable/placementOverviewTable';
import TabGroup from './tabGroup/tabGroup';

const isTabValid = (tab: string) =>
  [
    'pendingPlacements',
    'activePlacements',
    'expiredPlacements',
    'closedPlacements',
    'transferredInPlacements',
  ].includes(tab);

/**
 * Default component.
 */
const PlacementsOverview = () => {
  const dispatch = useAppDispatch();
  const { tab, site } = useParams();
  const { searchField, isUserDaliUser, appliedFilterOptions, currentPage, totalPages, perPage, filterTags } =
    useAppSelector((state) => state.placementOverviewPage);
  const navigate = useNavigate();
  const { userLoading, alias } = useAppSelector((state) => state.user);
  const { selectedSite, allSites, allowedSites } = useAppSelector((state) => state.sites);
  const { featureFlags } = useAppSelector((state) => state.user);
  const [currentTab, setCurrentTab] = useState(tab || 'pendingPlacements');
  const siteRef = useRef<string | undefined>(site);

  // Logic to do when site updates
  useEffect(() => {
    if (userLoading === 'fulfilled' && selectedSite.site) {
      if (selectedSite.site && selectedSite.site !== siteRef.current) {
        siteRef.current = selectedSite.site;
        navigate(`/placementsOverview/${siteRef.current}/pendingPlacements`);
      }
      if (!allowedSites.map((allowedSite) => allowedSite.site).includes(siteRef.current!)) {
        navigate('unauthorized');
      }
      /**
       * We would normally do this
       * const isUserDaliUser = isDaliUser(departmentCode);
       * but due to this issue, we are temporarily going with
       * a single experience for both DALI and non DALI users
       * https://issues.amazon.com/P70918518
       */
      const isUserDaliUser = false;
      if (isUserDaliUser) {
        dispatch(getOpenPlacementsByRequesterLogin(alias));
        dispatch(setIsSiteSelectionDisabled(true));
      } else {
        dispatch(getOpenAndClosedPlacementsBySiteId(selectedSite.id));
      }
      dispatch(setIsDaliUser(isUserDaliUser));
    }
    return () => {
      dispatch(setIsSiteSelectionDisabled(false));
    };
  }, [userLoading, selectedSite]);

  // Set site object
  useEffect(() => {
    if (siteRef && allSites.length > 0) {
      const siteObj = allSites.filter((s) => s.site === siteRef.current)[0];
      if (siteObj) {
        dispatch(setSelectedSite(siteObj));
        dispatch(setPreferredSite(siteObj.id));
        if (tab && currentTab !== tab && isTabValid(tab)) {
          navigate(`/placementsOverview/${siteObj.site}/${currentTab}`);
          dispatch(setCurrentPage(1));
        }
        if (tab && !isTabValid(tab)) {
          setCurrentTab('pendingPlacements');
          navigate(`/placementsOverview/${siteObj.site}/pendingPlacements`);
          dispatch(setCurrentPage(1));
        }
      }
    }
  }, [tab, siteRef, allSites, currentTab, dispatch, navigate]);

  return (
    <div className="placements-overview">
      {selectedSite.jobModificationsEnabled && (
        <NewFeatureBanner
          label="jobModificationsFeatureAlert"
          text={
            <>
              Pre-approved job modifications are now available in the RTW tool to support efficient placement decisions.
            </>
          }
        />
      )}
      {featureFlags.T2_PLUS_EXPERIENCE && (
        <NewFeatureBanner
          label="t2ExperienceFeatureAlert"
          text={<>The RTW tool now supports accommodation placement entry for associates at all job levels.</>}
        />
      )}
      <Column spacingInset="none none 400 none">
        <Heading level={1}>{isUserDaliUser ? `My Placements` : `Placements Overview for ${selectedSite.site}`}</Heading>
        <Row>
          <TabGroup currentTab={currentTab} setCurrentTab={setCurrentTab} />
          <Button type="primary" onClick={() => navigate('/newJMR')}>
            New Job Match Report
          </Button>
        </Row>

        <Box type="fill" spacingInset="300">
          <Row widths={['grid-3', 'fit']}>
            <SearchField
              value={searchField}
              onChange={(newSearchField) => dispatch(setSearchField(newSearchField))}
              placeholder="Search for..."
              onSubmit={() => {}}
            />
            {currentTab !== 'transferredInPlacements' && (
              <ToggleFilterButton
                data-testid="placements-overview__filter-icon"
                tooltipTitle="Open Placements Overview Filter"
                onClick={() => {
                  dispatch(setIsAdvancedFilterSheetOpen(true));
                  dispatch(setSelectedFilterOptions(appliedFilterOptions));
                }}
              />
            )}
          </Row>
        </Box>
        <AppliedFilterTags filterTags={filterTags} onDismissTag={(tag) => dispatch(removeTagAndUpdateFilters(tag))} />
        <PlacementOverviewTable currentTab={currentTab} />
        <AdvancedFilterSheet />
        <PaginationWithDropdown
          currentPage={currentPage}
          totalPages={totalPages}
          perPage={perPage}
          setPerPage={(newPerPage) => dispatch(setPerPage(newPerPage))}
          setCurrentPage={(newPage) => dispatch(setCurrentPage(newPage))}
        />
      </Column>
    </div>
  );
};

export default PlacementsOverview;
